<script lang="ts">
  // (c) Cognisoft Aps, 2022, for Svelte 5: 2026
  // written command, choose picture with sentence
  // correct sentence flies to top
  // error: red framed, "try again"
  // goes to step 4 after correct

  import { crossfade } from "svelte/transition";
  import { flip } from "svelte/animate";

  import { onDestroy } from "svelte";

  import { Pages, DaDK, Path, Extension, State } from "../enums";
  import { HelpLevel } from "../classes";
  import { afterCorrectWaitSecs, firstWaitSecs } from "../constants";

  import { Icons } from "../utilities/icons";
  import { shuffleArray } from "../utilities/shuffleArray";
  import { playSoundWithAudioTag } from "../utilities/playSound";
  import { playCorrectRandomWithAudioTag } from "../utilities/playCorrectRandom";
  import { getTotalNumberOfTasks } from "../utilities/getTotalNumberOfTasks";
  import { setCompletedBackgroundColor } from "../utilities/setCompletedBackgroundColor";
  import { logger } from "../utilities/logger";

  import { studentUserData } from "../stores/user-store.svelte";
  import { targetVerbAndObjects } from "../globals/targets";
  import { route } from "../stores/route.svelte";
  import { progress } from "../stores/progress.svelte";

  import { helpCount } from "../stores/helpCount.svelte";
  import { result } from "../stores/result.svelte";
  import { tasksCompleted } from "../stores/tasksCompleted.svelte";

  import ExitModal from "../modals/ExitModal.svelte";

  import RoundButton from "../components/RoundButton.svelte";
  import ProgressBar from "../components/ProgressBar.svelte";
  import PauseMessage from "../components/PauseMessage.svelte";

  // PROPS --------------------------------------------------

  // one audiotag in App.svelte is used on all pages in order to have
  //   sound played even before mouse click

  let { audioTag }: { audioTag: HTMLAudioElement } = $props();

  // CONSTANTS --------------------------------------------

  // needed for Svelte animation
  const [send, receive] = crossfade({});

  const numberOfHelpLevels = 6;

  // STATE ------------------------------------------------

  // complicated currentState because of levels of help both timed and requested

  let currentState: State = $state(State.WaitToStart); // initial currentState and currentState during presentation
  let stage: 0 | 1 | 2 | 3 = $state(0); // 0-3, one stage for each sentence presented
  let targets = $state([0, 1, 2, 3]); // is randomized at init
  let clickedItem = $state(-1); // -1: no image clicked, otherwise index of clicked image

  // arrays for send and received sentences
  //  needed for animation to work
  let sendItems: Array<Array<string>> = $state([[]]);
  let receiveItems: Array<string> = $state([]);

  let targetFound = $state([false, false, false, false]);

  let allowRepetition = $state(false); // repeat button not active from start
  let requestedHelpPaused = $state(false); // requested help not active for some seconds after activated last time

  let repeatButtonTimer: ReturnType<typeof setTimeout>; // repeat button not active from start
  let helpTimer: ReturnType<typeof setTimeout>; // help given after no response for a while
  let playNextAudioTimer: ReturnType<typeof setTimeout>; // to wait before next task

  // help both after a set time and requested - levels interact!
  let helpLevel = $state(new HelpLevel(numberOfHelpLevels));
  // enable images to wiggle for help for each of 4 items
  let imgWiggle = $state([false, false, false, false]);
  // enable finger pointing help for each of 4 items
  let fingerPointing = $state([false, false, false, false]);

  // for results:
  let gotHelpForItem = false;
  let hasErrorForItem = false;

  // FOR RESEARCH ONLY --------------------------------------------
  // // save results for each response, push to array in results.store
  // let presentTime: Date;
  // let numberOfTimedHelp = 0;
  // let numberOfRequestedHelp = 0;
  // let numberOfRequestedRepetitions = 0;
  // let attempt = 1;
  // let responseDetails: Array<ResponseDetail> = [];

  // keep track of pause time to subtract from total time in results
  let pauseStartTime: number;
  let pauseEndTime: number;

  // EFFECTS --------------------------------------------------

  // logging for debugging, set by env variable read in Ready.svelte

  $effect(() => {
    logger("Step3 currentState:", currentState);
  });
  $effect(() => {
    logger("Step3 stage:", stage);
  });
  $effect(() => {
    logger("Step3 helpLevel:", helpLevel.level);
  });

  // FUNCTIONS --------------------------------------------

  // help functions ---------------------------------------

  const countHelp = () => {
    switch (stage) {
      case 0:
        helpCount.value.reading.item31++;
        break;
      case 1:
        helpCount.value.reading.item32++;
        break;
      case 2:
        helpCount.value.reading.item33++;
        break;
      case 3:
        helpCount.value.reading.item34++;
        break;
      default:
        console.error("Step 3, countHelp, unknown stage:", stage);
    }
  };

  // show repeat button a defined time after showing task
  const startRepeatButtonTimer = () => {
    if (currentState === State.WaitAfterEnd) return;
    allowRepetition = false;
    repeatButtonTimer = setTimeout(() => {
      allowRepetition = true;
    }, studentUserData.value.settings.repeatDelaySecs * 1000);
  };

  // showing help according to 2 level hierarkies (timed and requested help) ---
  const startHelpTimerAndWaitForResponse = () => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    requestedHelpPaused = false;
    helpTimer = setTimeout(() => {
      timedHelp();
    }, studentUserData.value.settings.helpDelaySecs * 1000);
  };

  // general instruction is repeated
  const generalInstructionHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(
      audioTag,
      DaDK.Step3Intro,
      afterGeneralInstructionHelp,
    );
  };

  const afterGeneralInstructionHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  const listenAgainHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.ListenAgain, listenAgainHelp2);
  };

  const listenAgainHelp2 = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  const giveMeSentenceHelp = () => {
    helpLevel.setNextLevel();
    playSoundWithAudioTag(audioTag, DaDK.HandMe, afterGiveMeSentenceHelp);
  };

  const afterGiveMeSentenceHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // image vibrates and get glowing shadow, sentence sound repeated
  const vibrationHelp = () => {
    // set leve for both types of help
    helpLevel.setNextLevel();
    imgWiggle[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // finger slids up to correct sentence, sentence sound repeated
  const fingerPointingHelp = () => {
    // After finger pointing help give correct answer and go on
    helpLevel.setNextLevel();
    fingerPointing[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // last help level is to provide correct answer
  const giveAnswerHelp = () => {
    countHelp();
    progress.value += 1;
    currentState = State.Correct;
    helpLevel.setNextLevel();
    setVariablesForCorrectResponse();
    // say correct sentence
    receiveItems.unshift(sendItems[targets[stage]][0]); // OBS: putting at start of array!
    sendItems[targets[stage]].pop();
    targetFound[targets[stage]] = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentenceOK,
      afterGiveAnswerHelp,
    );
  };

  const afterGiveAnswerHelp = () => {
    // next picture or task
    helpTimer = setTimeout(() => {
      requestedHelpPaused = true;
      afterCorrectResponseOrLastHelpLevel();
    }, studentUserData.value.settings.helpDelaySecs * 500); // half of help delay
  };

  // give help after clicking help button according to help level
  const afterErrorHelp = () => {
    currentState = State.GivingHelp;
    logger("Step3 afterErrorHelp");
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    countHelp();
    gotHelpForItem = true;
    requestedHelpPaused = true;
    switch (helpLevel.level) {
      case 1:
        listenAgainHelp();
        break;
      case 2:
        giveMeSentenceHelp();
        break;
      case 3:
        giveMeSentenceHelp();
        break;
      case 4:
        vibrationHelp();
        break;
      case 5:
        fingerPointingHelp();
        break;
      case 6:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "error in errorHelp() switch, helpLevel: ",
          helpLevel.level,
          " stage: ",
          stage,
        );
    }
  };

  // give help after a duration according to help level
  const timedHelp = () => {
    currentState = State.GivingHelp;
    logger("Step3 timedHelp");
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    countHelp();
    // numberOfTimedHelp++;  // FOR RESEARCH ONLY
    gotHelpForItem = true;
    requestedHelpPaused = true;
    switch (helpLevel.level) {
      case 1:
        generalInstructionHelp();
        break;
      case 2:
        giveMeSentenceHelp();
        break;
      case 3:
        giveMeSentenceHelp();
        break;
      case 4:
        vibrationHelp();
        break;
      case 5:
        fingerPointingHelp();
        break;
      case 6:
        giveAnswerHelp();
        break;
      default:
        console.error(
          "error in timedHelp() switch, helpLevel: ",
          helpLevel.level,
          " stage: ",
          stage,
        );
    }
  };

  const requestedHelp = () => {
    currentState = State.RepeatingTask;
    clearTimeout(helpTimer);
    logger("Step3 requestedHelp");
    countHelp();
    // numberOfRequestedHelp++;  // ROR RESEARCH ONLY
    requestedHelpPaused = true;
    playSoundWithAudioTag(audioTag, DaDK.Step3Help, afterRequestedHelp);
  };

  const afterRequestedHelp = () => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // presentation of task and progression ------------------------------------

  // pause before sentence is played
  const waitAndStart = (): void => {
    playNextAudioTimer = setTimeout(() => {
      playIntro();
    }, firstWaitSecs);
  };

  const playIntro = (): void => {
    currentState = State.PlayIntro;
    playSoundWithAudioTag(audioTag, DaDK.Step3Intro, waitBeforeTask);
  };

  const waitBeforeTask = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.PresentTask;
    playNextAudioTimer = setTimeout(() => {
      askForSentenceText1();
    }, firstWaitSecs);
  };

  // ask for sentence, "hand me..."
  const askForSentenceText1 = (): void => {
    playSoundWithAudioTag(audioTag, DaDK.HandMe, askForSentenceText2);
  };

  // sentence asked for
  const askForSentenceText2 = (): void => {
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      WaitForResponse,
    );
  };

  // image and repeat buttons active while waiting for answer
  const WaitForResponse = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    currentState = State.WaitForResponse;
    startRepeatButtonTimer();
    startHelpTimerAndWaitForResponse();
    // initializeResultVariables(); // FOR RESEARCH ONLY
  };

  // common reset for chosen correct response and given as last stage of help
  const setVariablesForCorrectResponse = () => {
    if (!gotHelpForItem && !hasErrorForItem) {
      result.value.readingCorrectFirstTry =
        result.value.readingCorrectFirstTry + 1;
    }
    progress.value += 1;
    progress;
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    allowRepetition = false;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    // attempt = 1; // FOR RESEARCH ONLY
  };

  // correct ----

  const correctResponse = (): void => {
    receiveItems.unshift(sendItems[clickedItem][0]); // OBS: putting at start of array!
    sendItems[clickedItem].pop();
    playCorrectRandomWithAudioTag(
      audioTag,
      afterCorrectResponseOrLastHelpLevel,
    );
  };

  // go to next target or next step
  const afterCorrectResponseOrLastHelpLevel = (): void => {
    if (currentState === State.WaitAfterEnd) return;
    tasksCompleted.value = tasksCompleted.value + 1;
    if (stage < 3) {
      currentState = State.PresentTask;
      stage++;
      helpLevel.reset();
      gotHelpForItem = false;
      hasErrorForItem = false;
      waitBeforeTask();
    } else {
      setCompletedBackgroundColor(true);
      currentState = State.WaitAfterEnd;
      // result.value.responseDetails.push(...responseDetails); // for research
      result.value.lastStepCompleted = 3;
      setTimeout(() => {
        route.current = Pages.Step4;
      }, afterCorrectWaitSecs);
    }
  };

  // error -----

  const afterError = (): void => {
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    allowRepetition = true;
    hasErrorForItem = true;
    imgWiggle = [false, false, false, false];
    fingerPointing = [false, false, false, false];
    afterErrorHelp();
  };

  // button event handling ------

  // an image button was clicked, response evaluated
  const imgClicked = (index: number): void => {
    clearTimeout(playNextAudioTimer);
    clickedItem = index;
    logger("Step3 clickedItem:", clickedItem);
    if (index === targets[stage]) {
      // saveResultVariablesWithCorrect(true);
      currentState = State.Correct;
      targetFound[index] = true;
      setVariablesForCorrectResponse();
      correctResponse();
    } else {
      // saveResultVariablesWithCorrect(false);
      // attempt++;  // FOR RESEARCH ONLY
      currentState = State.Error;
      afterError();
    }
  };

  // when repeat button is pressed
  const repeatSound = () => {
    currentState = State.RepeatingSound;
    countHelp();
    // numberOfRequestedRepetitions++;  // FOR RESEARCH ONLY
    clearTimeout(helpTimer);
    requestedHelpPaused = true;
    playSoundWithAudioTag(
      audioTag,
      targetVerbAndObjects.value[targets[stage]].sentence,
      startHelpTimerAndWaitForResponse,
    );
  };

  // pause --------

  const pauseClicked = (): void => {
    if (currentState === State.Paused) {
      pauseEndTime = Date.now();
      result.value.pauseElapsed =
        result.value.pauseElapsed + Math.abs(pauseEndTime - pauseStartTime);
      allowRepetition = true;
      startHelpTimerAndWaitForResponse();
    } else {
      pauseStartTime = Date.now();
      clearTimeout(playNextAudioTimer);
      clearTimeout(repeatButtonTimer);
      clearTimeout(helpTimer);
      currentState = State.Paused;
    }
  };

  // exit task prematurely after stop button clicked

  // stop button: show yes-no modal
  const exitButtonClicked = () => {
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    currentState = State.ExitModalShown;
  };

  // exit task (modal event "yes")
  const exitTask = () => {
    currentState === State.WaitAfterEnd;
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
    clearTimeout(helpTimer);
    result.value.wasInterrupted = true;
    route.current = Pages.EndResults;
  };

  // dismiss exit modal (event "no")
  const dismissExitModal = () => {
    allowRepetition = true;
    startHelpTimerAndWaitForResponse();
  };

  // FOR RESEARCH ONLY --------------------------------------------

  // save result details for a single item, used for cvs results
  // *** used for research disabled in current production

  // const saveResultVariablesWithCorrect = (correct: boolean) => {
  // // for research - disabled in current production
  // responseDetails.push({
  //   step: 3,
  //   attempt: attempt,
  //   presentTime: presentTime,
  //   responseTime: new Date(),
  //   targetItem: targetVerbAndObjects.value[targets[stage]].sentence,
  //   correct: correct,
  //   numberOfTimedHelp: numberOfTimedHelp,
  //   numberOfRequestedHelp: numberOfRequestedHelp,
  //   numberOfRequestedRepetitions: numberOfRequestedRepetitions,
  // });
  // };

  // LIFE CYCLE ---------------------------------------------------

  onDestroy(() => {
    clearTimeout(helpTimer);
    clearTimeout(playNextAudioTimer);
    clearTimeout(repeatButtonTimer);
  });

  // INITIALIZE --------------------------------------------

  // FOR RESEARCH ONLY
  // const initializeResultVariables = () => {
  //   presentTime = new Date();
  //   numberOfTimedHelp = 0;
  //   numberOfRequestedHelp = 0;
  //   numberOfRequestedRepetitions = 0;
  // };

  setCompletedBackgroundColor(false);
  // initializeResultVariables();  // FOR RESEARCH ONLY
  // randomize order pictures are selected for targets
  shuffleArray(targets);
  // prepare array for send items used for animation
  for (let i = 0; i < targets.length; i++) {
    sendItems.push([]);
    sendItems[i].push(targetVerbAndObjects.value[i].sentence);
  }
  waitAndStart();
</script>

<!-- HTML ===================================================== -->

<main data-page="step3">
  <!-- data for Playwright tests -->
  <div
    data-testid="state"
    data-state={currentState}
    style="display: none;"
  ></div>
  <div
    data-testid="index"
    data-index={targets[stage]}
    style="display: none;"
  ></div>
  <!-- top panel with buttons for help and repeat sentence
    also receiving hand image - and correct sentence animated 
    to this position -->
  <div class="top-panel">
    <div>
      <span class="stop-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse}
          onclick={exitButtonClicked}
          icon={Icons.TrafficLightRed}
        />
      </span>
      <RoundButton
        disabled={currentState !== State.WaitForResponse &&
          currentState !== State.Paused}
        onclick={pauseClicked}
        icon={currentState === State.Paused ? Icons.Play : Icons.Pause}
      />
    </div>
    <div class="target-div">
      <!-- #each even though only one at a time - in order to be svelte animated  -->
      {#each receiveItems as item, i (item)}
        <div
          class="sentence target-{i}"
          class:wide-sentence={item.length > 15}
          animate:flip
          in:receive|global={{ key: item }}
        >
          <div>
            {item}
          </div>
        </div>
      {/each}
    </div>
    <div>
      <RoundButton
        disabled={currentState !== State.WaitForResponse || !allowRepetition}
        onclick={repeatSound}
        icon={Icons.VolumeRegular}
      />
      <span class="help-button">
        <RoundButton
          disabled={currentState !== State.WaitForResponse ||
            requestedHelpPaused}
          onclick={requestedHelp}
          icon={Icons.QuestionSolid}
        />
      </span>
    </div>
  </div>
  <!-- 4 clickable image buttons in 2x2 grid -->
  {#each targetVerbAndObjects.value as target, i}
    <button
      data-button-index={i}
      style="background-image: url('{encodeURI(
        Path.ImgBg + target.image + Extension.ImgBg,
      )}'); 
      background-image: image-set('{encodeURI(
        Path.ImgBg + target.image + Extension.WebpBg,
      )}'); background-size: cover;"
      class={"img img" + i}
      class:img-wiggle={imgWiggle[i]}
      class:img-shadow-glow={imgWiggle[i]}
      class:img-wait={currentState !== State.WaitForResponse}
      class:img-correct={(currentState === State.Correct ||
        currentState === State.WaitAfterEnd) &&
        i === targets[stage]}
      class:img-error={currentState === State.Error && clickedItem === i}
      disabled={currentState !== State.WaitForResponse}
      onclick={() => imgClicked(i)}
    >
      <div class="button-text" class:no-border={targetFound[i]}>
        {#if !targetFound[i]}
          <!-- #each even though only one at a time - in order to be svelte animated  -->
          {#each sendItems[i] as item (item)}
            <div animate:flip out:send|global={{ key: item }}>
              {item}
            </div>
          {/each}
        {/if}
      </div>
      <picture>
        <source
          type="image/webp"
          srcset={encodeURI(Path.Img + target.image + Extension.Webp)}
          draggable={false}
        />
        <img
          draggable={false}
          src={encodeURI(Path.Img + target.image + Extension.Png)}
          alt=""
        />
      </picture>
    </button>
    <div
      class={"finger-img finger-img-" + i}
      class:finger-point={fingerPointing[i]}
    >
      <picture class="finger-png">
        <source
          type="image/webp"
          srcset={encodeURI("finger-2.webp")}
          draggable={false}
        />
        <img draggable={false} src={"finger-2.png"} alt="" />
      </picture>
    </div>
  {/each}
  <div class="progress">
    <ProgressBar
      max={getTotalNumberOfTasks(studentUserData.value.currentLevel)}
      position={progress.value}
    />
  </div>

  <!-- MODALS -------------------------------------- -->
  {#if currentState === State.Paused}
    <PauseMessage pauseMessageClicked={pauseClicked} />
  {/if}

  {#if currentState === State.ExitModalShown}
    <ExitModal yes={exitTask} no={dismissExitModal} />
  {/if}
</main>

<!-- CSS ===================================================== -->
<style>
  main {
    margin-left: auto;
    margin-right: auto;
    width: 120vh;
    height: calc(var(--vh, 1vh) * 100);
    display: grid;
    grid-template-columns: 49% 49%;
    grid-template-rows: 14% 41% 41% 2%;
    grid-template-areas:
      "top top"
      "img1 img2"
      "img3 img4"
      "prog prog";
    gap: 2%;
    padding: 3%;
    padding-top: 1%;
  }
  @media screen and (max-aspect-ratio: 6/5) {
    main {
      width: 100vw;
    }
  }
  .top-panel {
    grid-area: top;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .target-div {
    position: relative;
    height: calc(var(--vh, 1vh) * 12);
    width: 50%;
    padding-right: 5%;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background-image: url("../hand-2.png");
    background-repeat: no-repeat;
    background-position: right;
    background-size: contain;
  }
  .img0 {
    grid-area: img1;
  }
  .img1 {
    grid-area: img2;
  }
  .img2 {
    grid-area: img3;
  }
  .img3 {
    grid-area: img4;
  }
  .progress {
    grid-area: prog;
    width: 100%;
  }
  img {
    width: 100%;
    height: 85%;
    object-fit: contain;
    object-position: bottom;
  }
  .finger-png {
    object-position: bottom right;
    height: 100%;
  }
  .finger-img {
    z-index: 2;
    transform: translate(-5%, 15%);
    visibility: hidden;
    pointer-events: none;
  }
  .finger-img-0 {
    grid-area: img1;
  }
  .finger-img-1 {
    grid-area: img2;
  }
  .finger-img-2 {
    grid-area: img3;
  }
  .finger-img-3 {
    grid-area: img4;
  }
  .sentence {
    position: absolute;
    border: solid 1px black;
    background: white;
    height: 15%;
    width: 20%;
    font-size: 1vmin;
    line-height: 2vmin;
    display: flex;
    align-items: right;
    justify-content: center;
    box-shadow: 1vmin 1vmin 1vmin hsl(0deg 0% 0% / 0.2);
    user-select: none;
  }
  .target-0 {
    margin-top: calc(var(--vh, 1vh) * -4);
    transform: translateX(5%);
    z-index: 4;
  }
  .target-1 {
    z-index: 3;
    margin-top: calc(var(--vh, 1vh) * -2);
    transform: translateX(10%);
  }
  .target-2 {
    z-index: 2;
    transform: translateX(15%);
  }
  .target-3 {
    margin-top: calc(var(--vh, 1vh) * 2);
    transform: translateX(20%);
    z-index: 1;
  }
  .button-text {
    border: solid 1px black;
    background: white;
    width: 100%;
    height: 15%;
    color: black;
    font-size: 4vmin;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
  }
  .no-border {
    border: none;
  }
  /* finger pointing help with animated finger sliding up */
  .finger-point {
    visibility: visible;
    animation-duration: 2s;
    animation-name: slideup;
  }
  @keyframes slideup {
    from {
      transform: translate(-5%, 50vh);
    }
    to {
      transform: translate(-5%, 15%);
    }
  }
</style>
